import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../database.generated";
import type { RepositoryResult } from "../contracts";
import { translateSupabaseError } from "./errors";

const SAFE_FUNCTION_MESSAGES = new Set([
  "The local diagnostic service is not configured.",
  "Rendered Storage availability could not be verified.",
  "Rendered Storage bytes could not be verified.",
  "The rendered Storage object is missing.",
  "Rendered Storage bytes do not match the file reservation.",
  "Rendered file not found.",
  "Rendered file is not eligible for trusted byte verification",
  "Rendered file changed during byte verification",
]);

const safeFunctionFailure = async (error: unknown) => {
  if (typeof error !== "object" || error === null) return error;
  const candidate = error as { context?: unknown; name?: unknown };
  const context = candidate.context;
  if (!(context instanceof Response)) return error;
  const body = await context.clone().json().catch(() => null) as { message?: unknown } | null;
  const message = typeof body?.message === "string" && SAFE_FUNCTION_MESSAGES.has(body.message)
    ? body.message
    : undefined;
  return {
    status: context.status,
    message,
    code: typeof candidate.name === "string" ? candidate.name : undefined,
  };
};

export async function verifyRenderedFileBytes(
  client: SupabaseClient<Database>,
  fileId: string,
): Promise<RepositoryResult<{ verified: true }>> {
  const { data, error } = await client.functions.invoke("rendered-presentation-diagnostic", {
    body: {
      schemaVersion: 1,
      operation: "verify-file",
      fileId,
    },
  });
  if (error) {
    return {
      ok: false,
      error: translateSupabaseError(
        await safeFunctionFailure(error),
        "Rendered PDF bytes could not be verified.",
      ),
    };
  }
  if (!data || data.verified !== true) {
    return {
      ok: false,
      error: {
        code: "validation_failure",
        message: "Rendered-file verification returned an invalid result.",
        retryable: false,
      },
    };
  }
  return { ok: true, value: { verified: true } };
}
